import { useEffect, useState } from "react";
import axios from "axios";
import { MapPin, Calendar, Play } from "lucide-react";
import VideoPlayer from "@/components/VideoPlayer";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const MARKET_HERO = "/market-queens-hero.webp";

// Add new campaign videos here as they come in.
const CAMPAIGN_FILMS = [
  {
    id: "makola-activation",
    location: "Makola Market",
    title: "Makola Market Activation",
    description:
      "On the ground with our Market Queens in Ghana's biggest market — putting Faytex directly into the hands of the women who move Accra.",
    src: "/makola-activation.mp4",
    poster: "/makola-poster.jpg",
  },
  {
    id: "yay-or-nay",
    location: "Accra",
    title: "Fay: Yay or Nay?",
    description:
      "A playful street-side vox pop — real reactions from Ghanaian women trying and rating Faytex on the spot.",
    src: "/yaynay-activation.mp4",
    poster: "/yaynay-poster.jpg",
  },
];

export default function MarketQueens() {
  const [queens, setQueens] = useState([]);

  useEffect(() => {
    axios.get(`${API}/market-queens`).then((r) => setQueens(r.data.queens || [])).catch(() => {});
  }, []);

  return (
    <div data-testid="market-queens-page" className="bg-white">
      <section className="relative isolate overflow-hidden">
        <div className="absolute inset-0 -z-10">
          <img src={MARKET_HERO} alt="Makola market" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-r from-black/65 via-black/40 to-transparent" />
        </div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 md:py-44 text-white">
          <p className="text-xs uppercase tracking-widest text-faytex-pink">Our Market Queens</p>
          <h1 className="font-heading text-4xl sm:text-5xl md:text-7xl mt-3 tracking-tighter max-w-4xl">
            The Women Who Carry Faytex Across Ghana
          </h1>
          <p className="mt-6 max-w-3xl text-white/85 text-lg leading-relaxed">
            Behind every Faytex product is a network of hardworking women who bring confidence to
            communities every day.
          </p>
          <p className="mt-4 max-w-3xl text-white/80 text-lg leading-relaxed">
            From Makola to Madina, Kejetia and markets across Ghana, our Market Queens and
            distributors ensure women have access to trusted menstrual care products.
          </p>
          <p className="mt-4 max-w-3xl text-white/80 text-lg leading-relaxed">
            They are entrepreneurs, community builders and an essential part of the Faytex story.
          </p>
        </div>
      </section>

      {/* Bento Grid */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 md:py-32">
        <div className="grid grid-cols-1 md:grid-cols-12 auto-rows-[260px] gap-6">
          {queens.map((q, idx) => (
            <article
              key={q.id}
              data-testid={`queen-card-${q.id}`}
              className={`relative rounded-3xl overflow-hidden group ${
                idx === 0 ? "md:col-span-7 md:row-span-2" :
                idx === 1 ? "md:col-span-5" :
                            "md:col-span-5"
              }`}
            >
              <img src={q.image} alt={q.name} className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
              <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/30 to-transparent" />
              <div className="absolute bottom-0 left-0 right-0 p-6 md:p-8 text-white">
                <div className="flex items-center gap-3 text-xs uppercase tracking-widest opacity-90">
                  <span className="inline-flex items-center gap-1"><MapPin className="w-3 h-3" /> {q.market}</span>
                  <span className="inline-flex items-center gap-1"><Calendar className="w-3 h-3" /> {q.years} yrs</span>
                </div>
                <h3 className="font-heading text-3xl md:text-4xl mt-2">{q.name}</h3>
                <p className="text-sm md:text-base text-white/85 mt-3 leading-relaxed max-w-xl line-clamp-3 md:line-clamp-none">
                  {q.story}
                </p>
              </div>
            </article>
          ))}
        </div>
      </section>

      {/* Campaign Films */}
      <section
        data-testid="campaign-films"
        className="bg-faytex-ink text-white"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 md:py-32">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6">
            <div className="max-w-2xl">
              <p className="text-xs uppercase tracking-widest text-faytex-pink font-semibold">
                Campaign Films
              </p>
              <h2 className="font-heading text-3xl sm:text-4xl md:text-5xl mt-3 tracking-tight">
                Faytex on the ground — market activations across Ghana.
              </h2>
            </div>
            <p className="text-sm text-white/70 max-w-sm">
              A growing archive of the market activations, community events and outreach campaigns
              that put Faytex directly in the hands of Ghanaian women.
            </p>
          </div>

          <div className="mt-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {CAMPAIGN_FILMS.map((film) => (
              <article
                key={film.id}
                data-testid={`campaign-film-${film.id}`}
                className="group"
              >
                <VideoPlayer
                  testid={`campaign-video-${film.id}`}
                  src={film.src}
                  poster={film.poster}
                  ariaLabel={`Play ${film.title}`}
                  className="rounded-3xl shadow-hover aspect-[9/16]"
                />
                <div className="mt-5">
                  <p className="inline-flex items-center gap-1 text-xs uppercase tracking-widest text-faytex-pink font-semibold">
                    <MapPin className="w-3.5 h-3.5" /> {film.location}
                  </p>
                  <h3 className="font-heading text-2xl mt-2">{film.title}</h3>
                  <p className="mt-3 text-sm text-white/70 leading-relaxed">{film.description}</p>
                </div>
              </article>
            ))}

            {/* Empty slots hint when only one film exists — encourages the "growing archive" feel */}
            {CAMPAIGN_FILMS.length < 3 &&
              Array.from({ length: 3 - CAMPAIGN_FILMS.length }).map((_, i) => (
                <div
                  key={`empty-${i}`}
                  className="hidden lg:flex flex-col items-center justify-center rounded-3xl border border-dashed border-white/15 aspect-[9/16] text-white/40"
                >
                  <Play className="w-8 h-8" />
                  <p className="mt-4 text-xs uppercase tracking-widest">More films coming soon</p>
                </div>
              ))}
          </div>
        </div>
      </section>
    </div>
  );
}
